import { COLLISION_COLOURS } from '../../constants/Constants';
import { CollisionCollider } from '../world/collisions/CollisionCollider';
import { CharacterStateMachine } from './statemachines/CharacterStateMachine';

const GRAVITY_UP = 2500;
const GRAVITY_DOWN = 3500;
export const JUMP_FORCE = 1250;
const LANE_WIDTH = 64;
const COOLDOWN_REQUEST_JUMP = 0.1;
const COOLDOWN_REQUEST_ATTACK = 0.1;
export const COOLDOWN_RESTRAINT = 5;
export const COOLDOWN_KNOCKDOWN = 2;

export class CharacterBase extends Phaser.GameObjects.Container {
  constructor(scene, x, y, config, collisionmap) {
    super(scene, x, y);

    scene.add.existing(this);

    this._config = config;
    this._collisionmap = collisionmap;

    // setup sprite
    if (config.spineid) {
      this._sprite = scene.add.spine(0, 0, config.spineid, CharacterBase.ANIMATIONS.IDLE, true);
      this._spriteSkins = this._sprite.getSkinList ? this._sprite.getSkinList() : [];
      this._spriteAnimations = this._sprite.getAnimationList ? this._sprite.getAnimationList() : [];

      this.sprite.on('event', this.handleSpineEvent, this);

      this.shadow = scene.add.image(0, 0, 'game_graphics', 'character_shadow').setOrigin(0.5);

      this.setSpriteSkin('normal');
      this.add([this.shadow, this._sprite]);
    } else this.createSprite();

    // colliders
    // damage collider
    this._baseSize = this._sprite.getBounds().size;

    // character config
    this._config = config;

    this._id = config.id;
    this._speedMove = config.speed || 300;
    this._healthMax = config.health;
    this._health = config.health;
    this._grabbable = true;

    this._stateMachine = this.createStateMachine();

    this._requestAttack = false;
    this._requestJump = false;
    this._velocity = { x: 0, y: 0, z: 0 };
    this._characterBounds = new Phaser.Geom.Rectangle();

    this._attackActive = false;
    this._jumpActive = false;
    this._faceDirection = 1;

    // cooldowns
    this._cooldownStagger = 0;

    this._bodyCollider = new CollisionCollider(scene, {
      x: this.x - this.width * 0.5,
      y: this.y - this.height,
      sensor: true,
      width: this.width,
      height: this.height,
      gameObject: this,
      debugColour: COLLISION_COLOURS.BODY,
      collisionGroup: config.collisionGroup || -1,
    });

    this._attackCollider = new CollisionCollider(scene, {
      x: this.x,
      y: this.y,
      sensor: true,
      width: this.width,
      height: this.height,
      gameObject: this,
      debugColour: COLLISION_COLOURS.ATTACK,
      enabled: false,
      collisionGroup: config.collisionGroup || -1,
    });

    this.setState(CharacterBase.STATES.IDLE);
    this._spriteBounds = this.sprite.getBounds();
  }

  handleSpineEvent(t, e) {
    switch (e.data.name) {
      case 'sfx':
        if (e.stringValue) this.playSFX(e.stringValue);
        break;
      case 'projectile':
        if (this._attackActive && this._attackActive.launchProjectile) {
          this._attackActive.launchProjectile();
        }
        break;
    }
  }

  get attackCollider() {
    return this._attackCollider;
  }

  get bodyCollider() {
    return this._bodyCollider;
  }

  createStateMachine() {
    return new CharacterStateMachine(this.scene, this);
  }

  get colliderAttack() {
    if (!this._attackActive || !this.isAttacking) return false;
    return this._attackActive.attackCollider;
  }

  get id() {
    return this._config.id;
  }

  get name() {
    return this._config.name || this.id;
  }

  get health() {
    return this._health;
  }

  get healthPercent() {
    return this._health / this._healthMax;
  }

  get healthMax() {
    return this._healthMax;
  }

  get movementSpeed() {
    return this._speedMove;
  }

  get attackRange() {
    return this._config.attackRange;
  }

  get attackRate() {
    return this._config.attackRate;
  }

  get stateMachine() {
    return this._stateMachine;
  }

  get state() {
    return this._stateMachine.state;
  }

  get sprite() {
    return this._sprite;
  }

  get isJumping() {
    return this._jumpActive;
  }

  get isAttacking() {
    return this.isInState(CharacterBase.STATES.ATTACK);
  }

  get isDead() {
    return this._health <= 0 || this.isInState(CharacterBase.STATES.RESPAWN);
  }

  get isRespawning() {
    return this.isInState(CharacterBase.STATES.RESPAWN);
  }

  get isFullyDead() {
    return this.isDead && (!this.sprite || this.sprite.alpha <= 0);
  }

  get isGrounded() {
    return this._sprite.y >= 0 && this._velocity.z >= 0;
  }

  get canDamage() {
    return (
      this.isInState([
        CharacterBase.STATES.IDLE,
        CharacterBase.STATES.RESTRAINT,
        CharacterBase.STATES.STAGGER,
      ]) ||
      (this.isInState([CharacterBase.STATES.KNOCKDOWN, CharacterBase.STATES.THROWN]) &&
        !this.isGrounded)
    );
  }

  get grabbable() {
    return this.canDamage && this._grabbable;
  }

  get velocity() {
    return this._velocity;
  }

  set velocity(value) {
    this._velocity = { ...this._velocity, ...value };
  }

  set faceDirection(value) {
    if (value) {
      const dir = value > 0 ? 1 : -1;
      this._faceDirection = dir;
      if (this._sprite.scaleX !== dir) this._sprite.scaleX = dir;
    }
  }

  get faceDirection() {
    return this._faceDirection || 1;
  }

  get activeAttack() {
    return this._attackActive;
  }

  get width() {
    return this._baseSize.x;
  }

  get height() {
    return this._baseSize.y;
  }

  // =============================================================================================
  // STATE MANAGEMENT
  // =============================================================================================

  setState(state, force) {
    this._stateMachine.setState(state, force);
  }

  setIdleState() {
    this.bodyCollider.enabled = true;
    this.setState(CharacterBase.STATES.IDLE);
    this.playAnimation(CharacterBase.ANIMATIONS.IDLE, true);
  }

  isInState(state) {
    return this.stateMachine?.isState(state);
  }

  canJump() {
    return this.isGrounded && this.isInState(CharacterBase.STATES.IDLE);
  }

  canAttack() {
    return this.isInState([CharacterBase.STATES.IDLE, CharacterBase.STATES.GRAB]);
  }

  // =============================================================================================
  // SPRITE MANAGAMENT
  // =============================================================================================

  createSprite() {
    console.warn('if no spineid is supplied then createSprite method should be overwritten');
  }

  setSpriteSkin(skin) {
    if (this._spriteSkins.includes(skin)) this._sprite.setSkinByName(skin);
  }

  spriteHasAnimation(animation) {
    return animation
      ? this._spriteAnimations.find((a) => a.toLowerCase() === animation.toLowerCase())
      : false;
  }

  getBounds() {
    return this._spriteBounds;
  }

  faceTarget(target) {
    this.faceDirection = target.x > this.x ? 1 : -1;
  }

  isFacingTarget(target) {
    return (
      (this.faceDirection > 0 && target.x > this.x) || (this.faceDirection < 0 && target.x < this.x)
    );
  }

  // =============================================================================================
  // ACTION REQUESTS
  // =============================================================================================

  requestJump() {
    this._requestJump = COOLDOWN_REQUEST_JUMP;
  }

  requestAttack(opts = {}) {
    this._requestAttack = { time: COOLDOWN_REQUEST_ATTACK, ...opts };
  }

  updateJumpRequest(delta) {
    if (this._requestJump && !this.isGrabbing) {
      this._requestJump = Math.max(this._requestJump - delta, 0);
      return this.triggerJump();
    }
    return;
  }

  triggerJump(force) {
    if (!this.canJump() && !force) return false;
    this._requestJump = false;
    this._jumpActive = true;
    this.setState('jumpstart');
    this.playAnimation(CharacterBase.ANIMATIONS.JUMP_IN, false, () => {
      this.setState(CharacterBase.STATES.IDLE);
      this._velocity.z = -JUMP_FORCE;
      this.playAnimation(CharacterBase.ANIMATIONS.JUMP_LOOP, false);
    });
    return true;
  }

  updateAttackRequest(delta) {
    if (this._requestAttack) {
      this._requestAttack.time -= delta;
      if (this._requestAttack.time <= 0) {
        this._requestAttack = false;
      }
      if (this.canAttack()) {
        this.triggerAttack();
        this._requestAttack = false;
      }
    }
  }

  // =============================================================================================
  // UPDATE AND MOVE
  // =============================================================================================

  update(delta) {
    this.updateDebug(delta);
    if (this._cooldownStagger > 0) return this.updateStagger(delta);

    this.updateStateMachine(delta);
    if (!this.isInState(CharacterBase.STATES.DEAD)) {
      this.updateSpriteBounds(delta);
      this.updateActionRequests(delta);
      this.updateBodyCollider(delta);
    }
  }

  updateStagger(delta) {
    this._cooldownStagger = Math.max(0, this._cooldownStagger - delta);
  }

  updateActionRequests(delta) {
    if (!this.updateJumpRequest(delta)) this.updateAttackRequest(delta);
  }

  updateStateMachine(delta) {
    this._stateMachine.update(delta);
  }

  updateSpriteBounds(delta) {
    const bounds = this._sprite.getBounds();
    this._spriteBounds.x = this.scene.cameras.main.scrollX + bounds.offset.x;
    this._spriteBounds.y = this.y + this._sprite.y - bounds.size.y;
    this._spriteBounds.width = bounds.size.x;
    this._spriteBounds.height = bounds.size.y;
    this._spriteBounds.depth = this.y;

    this.shadow.x = this._spriteBounds.x - this.x + this._spriteBounds.width * 0.5;
    this.shadow.displayWidth = this._spriteBounds.width * 1.25;
  }

  updateBodyCollider(delta) {
    if (this._config.bodyCollider) {
      this.bodyCollider.x =
        this.x -
        this._config.bodyCollider.width * 0.5 +
        (this._config.bodyCollider.offsetX || 0) * this.faceDirection;
      this.bodyCollider.y = this.y - this._config.bodyCollider.height + this.sprite.y;
      this.bodyCollider.width = this._config.bodyCollider.width;
      this.bodyCollider.height = this._config.bodyCollider.height;
    } else {
      this.bodyCollider.x = this._spriteBounds.x;
      this.bodyCollider.y = this._spriteBounds.y;
      this.bodyCollider.width = this._spriteBounds.width;
      this.bodyCollider.height = this._spriteBounds.height;
    }

    if (this.isInState(CharacterBase.STATES.KNOCKDOWN)) {
      const { x, y, width, height } = this.bodyCollider;
      this.bodyCollider.width = height;
      this.bodyCollider.height = width;
      this.bodyCollider.x = this.x;
      this.bodyCollider.y = y + height - width;
    }
    this.bodyCollider.depth = this.y;
  }

  updateFaceDirection(delta) {
    this.faceDirection = this._velocity.x;
  }

  updateMovement(delta) {
    if (this.path?.length) {
      this.updatePath(delta);
    } else {
      this.updatePosition({
        x: this.velocity.x * delta,
        y: this.velocity.y * delta,
      });
    }

    this.shadow.alpha = 1;
    this.shadow.scaleY = 1;

    // handle vertical velocity
    if (this._velocity.z != 0) this._sprite.y += this._velocity.z * delta;
    // check if airborne
    if (this._sprite.y < 0) {
      this._velocity.z += (this._velocity.z > 0 ? GRAVITY_UP : GRAVITY_DOWN) * delta;
      this.shadow.alpha = 1 - Math.min(Math.max(0, Math.abs(this.sprite.y) / 200), 1);
    }

    // check if grounded
    else if (this._sprite.y >= 0) {
      this._sprite.y = 0;

      if (this._velocity.z > 0) {
        // 'land' the character
        if (this.isInState([CharacterBase.STATES.THROW, CharacterBase.STATES.KNOCKDOWN])) {
          this.scene.playSFX('throw_land', { lastPlayedMin: 275 });
        }

        const isKnockeddown = this.isInState(CharacterBase.STATES.KNOCKDOWN);
        if (isKnockeddown && this._velocity.z > 500) {
          this._velocity.z = -Math.abs(this._velocity.z * 0.5);
        } else {
          this._velocity.z = 0;
          if (!this._attackActive && !isKnockeddown) {
            // this.velocity.x = this.velocity.y = 0;
            this.playAnimation(CharacterBase.ANIMATIONS.JUMP_OUT, false, () => {
              this._jumpActive = false;
            });
          } else this._jumpActive = false;
        }
      }
    }
  }

  updatePath(delta) {
    if (this._path?.length) {
      // get target position and create direction velocity
      const target = this._path[0];

      // if (this._type === 'enemy') {
      //   if (!this._targetDebug) this._targetDebug = this.scene.add.graphics();
      //   this._targetDebug.clear();
      //   this._targetDebug.fillStyle(0xff0000, 0.75);
      //   this._targetDebug.lineStyle(2, 0xff0000);
      //   this._targetDebug.moveTo(this._path[0].x, this._path[0].y);
      //   this._path.forEach((p) => {
      //     this._targetDebug.lineTo(p.x, p.y);
      //   });
      //   this._targetDebug.stroke();
      //   this._path.forEach((p) => {
      //     this._targetDebug.fillCircle(p.x, p.y, 5);
      //   });
      // }

      const direction = new Phaser.Math.Vector2(target.x - this.x, target.y - this.y);
      direction.normalize();
      const deltaDirX = direction.x > 0 ? 1 : -1;
      const deltaDirZ = direction.y > 0 ? 1 : -1;
      const targetDistX = Math.abs(this.x - target.x);
      const targetDistY = Math.abs(this.y - target.y);
      const deltaStepX = Math.min(targetDistX, Math.abs(direction.x * this._speedMove) * delta);
      const deltaStepY = Math.min(targetDistY, Math.abs(direction.y * this._speedMove) * delta);

      if (deltaStepX >= targetDistX && deltaStepY >= targetDistY) {
        this.x = target.x;
        this.y = target.y;
        this._path.shift();
        if (this.path.length === 0) {
          this.velocity.x = this.velocity.y = 0;
          return;
        }
      }
      this.velocity.x = direction.x * this._speedMove;
      this.velocity.y = direction.y * this._speedMove;
      return this.updatePosition({ x: deltaStepX * deltaDirX, y: deltaStepY * deltaDirZ });
    }

    this.velocity.x = this.velocity.y = 0;
  }

  updatePosition(velocity) {
    velocity = { ...velocity };

    // update horizontal movement if applies
    if (velocity.x != 0) {
      const dirX = velocity.x > 0 ? 1 : -1;
      // this.x += velocity.x;
      if (!this._collisionmap.canMoveHorizontal(this.x, this.y, dirX, this._type === 'enemy')) {
        velocity.x = 0;
      }
    }

    // update depth movement if applies
    if (velocity.y != 0) {
      const dirY = velocity.y > 0 ? 1 : -1;
      if (!this._collisionmap.canMoveVertical(this.x, this.y, dirY, this._type === 'enemy')) {
        velocity.y = 0;
      }
    }
    this.x += Math.round(velocity.x);
    this.y += velocity.y;
  }

  updateDebug() {
    if (this.scene.game.model.showDebugCharacterStates) {
      if (!this.debugText) {
        this.debugText = this.scene.add
          .text(0, 20, '', {
            fontFamily: 'Arial',
            fontSize: '24px',
            color: '#ffffff',
            align: 'center',
          })
          .setOrigin(0.5, 0);
        this.add(this.debugText);
      }
      this.debugText.text = this.stateMachine.state;
      if (this._attackActive) {
        this.debugText.text += '\n- ' + this._attackActive.name + ' -';
      }
    } else if (this.debugText) {
      this.debugText.destroy();
      this.debugText = null;
    }
  }

  // =============================================================================================
  // ANIMATIONS
  // =============================================================================================

  playAnimation(animation, loop, callback, callbackscope) {
    if (Array.isArray(animation)) {
      const target = animation.find((a) => this.spriteHasAnimation(a));
      if (target) animation = target;
      else animation = null;
    }

    if (!this.spriteHasAnimation(animation)) {
      if (callback) callback.apply(callbackscope);
      return;
    }
    this._sprite.removeAllListeners('complete');
    if (callback) this._sprite.once('complete', callback, callbackscope);
    this._sprite.play(animation, loop, true);
  }

  // =============================================================================================
  // ATTACK MANAGEMENT
  // =============================================================================================

  triggerAttack() {
    if (this._attackActive) return;
    const attack = this.chooseAttack();
    if (attack) {
      this.activateAttack(attack);
    }
  }

  activateAttack(attack) {
    this._attackActive = attack;
    this.clearPath();

    if (this.isGrounded) {
      this._velocity.x = this._velocity.y = 0;
    }

    this._attackActive.trigger();
    this.setState(CharacterBase.STATES.ATTACK);
  }

  chooseAttack() {}

  handleAttackComplete(attack) {
    this.bodyCollider.enabled = true;
    this._attackActive = null;
    this.setIdleState();
  }

  cancelAttack() {
    this._attackActive = null;
    this.setIdleState();
  }

  heal(value) {
    this._health = Math.min(this._health + value, this._healthMax);
  }

  damage(value, opts = {}) {
    if (this.canDamage) {
      this._health -= value;
      if (this._health <= 0) {
        this._health = 0;
        this.die();
      } else {
        // if knockdown option, just trigger knockdown state
        if (opts.knockdown || this.isInState(CharacterBase.STATES.KNOCKDOWN)) {
          this.knockdown();
        } else {
          this.setState(CharacterBase.STATES.DAMAGE);
          // otherwise trigger 'hit' animation based on if currently restrained
          const triggerAnim = this.isInState(CharacterBase.STATES.RESTRAINT)
            ? CharacterBase.ANIMATIONS.RESTRAINT_HIT
            : CharacterBase.ANIMATIONS.STAGGER;
          this.playAnimation(
            triggerAnim,
            false,
            this.stateMachine.setPreviousState,
            this.stateMachine
          );
          // if stagger is set, then apply stagger cooldown
          if (opts.stagger) this._cooldownStagger = this._config.stagger + 1;
        }
      }
      this.emit('DAMAGE_RECIEVED', value);
    }
  }

  knockdown() {
    if (this.isDead || this.isInState(CharacterBase.STATES.KNOCKDOWN)) return;
    this.setState(CharacterBase.STATES.KNOCKDOWN);
    this.playAnimation(CharacterBase.ANIMATIONS.KNOCKDOWN);
    this.clearPath();
    this._velocity.x = 500 * -this.faceDirection;
    this._velocity.z = -1000;
    this._velocity.y = 0;
    this.sprite.y -= 50;
    this._cooldownStagger = 0;
    // this.bodyCollider.enabled = false;
  }

  restrain() {
    this._velocity.x = 0;
    this._velocity.y = 0;
    this.clearPath();
    this.setState(CharacterBase.STATES.RESTRAINT, true);
    this.playAnimation(CharacterBase.ANIMATIONS.RESTRAINT);
  }

  standUp() {
    this.setState(CharacterBase.STATES.STANDUP);
    this.playAnimation('standing', false, this.setIdleState, this);
  }

  release() {
    if (this.isInState(CharacterBase.STATES.RESTRAINT)) this.setIdleState();
  }

  throw(direction = 1) {
    this.bodyCollider.enabled = false;
    this.setState(CharacterBase.STATES.THROWN);
    this.playAnimation(CharacterBase.ANIMATIONS.THROWN);
    this._velocity.x = 1500 * direction;
    this._velocity.z = -1000;
    this._velocity.y = 0;
  }

  die() {
    if (this.isInState(CharacterBase.STATES.DEAD)) return;
    this.clearPath();
    this.bodyCollider.enabled = false;
    this.velocity.x = this.velocity.y = 0;
    this.bodyCollider.enabled = false;
    this.setState(CharacterBase.STATES.DEAD);
    this.playAnimation(CharacterBase.ANIMATIONS.DEAD);
    this.playSFX('death_disappear');
  }

  playSFX(id, sprite) {
    this.scene.game.soundController.playSFX(sprite || 'sfx', id);
  }

  // =============================================================================================
  // PATH FINDING
  // =============================================================================================

  get path() {
    return this._path;
  }

  findPath(from, to, force) {
    if (this._pathSearching && !force) return;
    this._pathSearching = true;
    this._collisionmap.findPath(from, to, this.setPath, this);
  }

  clearPath() {
    this._path = null;
  }

  setPath(path) {
    this._pathSearching = false;
    this._path = path;
  }

  targetInLane(target) {
    return Math.abs(target.y - this.y) < LANE_WIDTH * 0.5;
  }

  // =============================================================================================
  // CLEAN UP
  // =============================================================================================

  destroy() {
    this.removeAllListeners();
    this._bodyCollider.destroy();
    this._bodyCollider = null;
    this._attackCollider.destroy();
    this._attackCollider = null;
    this._stateMachine.destroy();
    this._stateMachine = null;
    this._config = null;
    this._collisionmap = null;
    this._sprite = null;
    this._spriteSkins = null;
    this._spriteAnimations = null;
    this._requestAttack = null;
    this._attackActive = null;
    if (this.debugText) {
      this.debugText.destroy();
      this.debugText = null;
    }
    super.destroy();
  }
}

CharacterBase.ANIMATIONS = {
  WALK: 'walk_e',
  IDLE: 'idle',
  KNOCKDOWN: 'hit_and_knocked_down',
  DEAD: 'hit_and_knocked_down',
  STAGGER: 'hit_and_stagger',
  GRAB: 'grab',
  THROW: 'throw',
  PICKUP: 'pick_up',
  RESTRAINT: 'grabbed',
  RESTRAINT_HIT: 'grabbed_hit',
  THROWN: 'throw',
  JUMP_IN: ['jump_in', 'in_jump'],
  JUMP_LOOP: ['jump_loop', 'loop_jump'],
  JUMP_OUT: ['jump_out', 'out_jump'],
};

CharacterBase.STATES = {
  IDLE: 'idle',
  ATTACK: 'attack',
  STAGGER: 'stagger',
  KNOCKDOWN: 'knockdown',
  DAMAGE: 'damage',
  DEAD: 'dead',
  RESTRAINT: 'restraint',
  GRAB: 'grab',
  PICKUP: 'pickup',
  THROWN: 'thrown',
  RESPAWN: 'respawn',
  THROW: 'throw',
  STANDUP: 'standup',
};

CharacterBase.EVENTS = {
  DEAD: 'DEAD',
  DEAL_DAMAGE: 'DEAL_DAMAGE',
};

CharacterBase.ATTACKS = {
  MELEE: 'MELEE',
  ARIAL: 'ARIAL',
  CHARGE: 'CHARGE',
  PROJECTILE: 'PROJECTILE',
};

class CharacterEvent extends Phaser.Events.EventEmitter {
  emit(evt, character, opts = {}) {
    super.emit(evt, character, opts);
  }
}
